import type { BootReporter } from "./assets";
import type { BootGroup, BootTaskId, ScannerEndpoint } from "../protocol/events";

interface AssetProgress {
  id: number;
  label: string;
  group: BootTaskId;
  status: BootGroup["status"];
  loaded: number;
  total: number;
}

const groups: Array<[BootTaskId, string]> = [
  ["detector", "Card detector"],
  ["recogniser", "Card recogniser"],
  ["index", "Card index & prices"],
  ["extras", "Extras"],
];

/** Keep byte accounting on the engine side and detailed events available for diagnostics. */
export function createBootReporter(endpoint: ScannerEndpoint): BootReporter {
  const assets = new Map<string, AssetProgress>();
  let nextId = 0;
  let fromCache = false;
  let planned = false;
  const publish = (): void => {
    const tasks = groups.map(([id, label]): BootGroup => {
      const members = [...assets.values()].filter((asset) => asset.group === id);
      const status = members.some((asset) => asset.status === "failed")
        ? "failed"
        : members.length && members.every((asset) => asset.status === "ready")
          ? "ready"
          : members.some((asset) => asset.status === "loading" || asset.status === "ready")
            ? "loading"
            : "queued";
      return {
        id,
        label,
        optional: id === "extras",
        status,
        loaded: members.reduce((sum, asset) => sum + asset.loaded, 0),
        total: members.reduce((sum, asset) => sum + asset.total, 0),
      };
    });
    const required = tasks.filter((task) => !task.optional);
    endpoint.emit("boot:state", {
      tasks,
      fromCache,
      loaded: planned ? required.reduce((sum, task) => sum + task.loaded, 0) : 0,
      total: planned ? required.reduce((sum, task) => sum + task.total, 0) : 0,
    });
  };
  const reporter: BootReporter = (label, options) => {
    let asset = assets.get(label);
    if (!asset) {
      asset = {
        id: nextId++,
        label,
        group: options?.group ?? "extras",
        status: "queued",
        loaded: 0,
        total: options?.total ?? 0,
      };
      assets.set(label, asset);
    }
    const current = asset;
    current.status = "loading";
    endpoint.emit("boot:task", { id: current.id, label });
    publish();
    return {
      progress(loaded, total, cached) {
        current.loaded = loaded;
        if (total > 0) current.total = total;
        current.total = Math.max(current.total, loaded);
        if (cached) fromCache = true;
        endpoint.emit("boot:progress", { id: current.id, loaded, total: current.total });
        publish();
      },
      done(note) {
        current.status = "ready";
        current.loaded = Math.max(current.loaded, current.total);
        endpoint.emit("boot:done", { id: current.id, note });
        publish();
      },
      fail(message) {
        current.status = "failed";
        endpoint.emit("boot:failed", { id: current.id, message });
        publish();
      },
    };
  };
  reporter.plan = (entries) => {
    planned = true;
    const labels = new Set(entries.map((entry) => entry.label));
    // A changed manifest or backend must not retain a previous attempt's file totals.
    for (const [label] of assets) if (!labels.has(label)) assets.delete(label);
    for (const entry of entries) {
      const previous = assets.get(entry.label);
      assets.set(entry.label, {
        id: previous?.id ?? nextId++,
        label: entry.label,
        group: entry.group,
        loaded: previous?.loaded ?? 0,
        total: entry.total || previous?.total || 0,
        status: previous?.status === "ready" ? "ready" : "queued",
      });
    }
    publish();
  };
  reporter.resetFailures = () => {
    for (const asset of assets.values()) {
      if (asset.status === "failed") asset.status = "queued";
    }
    publish();
  };
  return reporter;
}
